import React, { useState, useEffect, useRef } from 'react';
import ExcelJS from 'exceljs';
import './ExportDropdown.scss';

// Inline premium SVGs for icons
const ExportIcon = () => (
  <svg className="export-dropdown__icon-main" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ArrowDownIcon = ({ isOpen }) => (
  <svg 
    className={`export-dropdown__icon-arrow ${isOpen ? 'export-dropdown__icon-arrow--open' : ''}`} 
    width="12" 
    height="12" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ExcelIcon = () => (
  <svg className="export-dropdown__item-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#107c41' }}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
  </svg>
);

const CSVIcon = () => (
  <svg className="export-dropdown__item-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#0078d4' }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const ChartIcon = () => (
  <svg className="export-dropdown__item-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ea580c' }}>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

export function ExportDropdown({ data, name, chartRef, chartRefs, className = '', style }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // 1. Export to Excel
  const handleExportExcel = async (e) => {
    e.stopPropagation();
    setIsOpen(false);
    try {
      const workbook = new ExcelJS.Workbook();
      // Sheet names in Excel cannot contain characters like *, ?, :, /, \, [, ] and must be <= 31 chars
      const safeSheetName = name.substring(0, 31).replace(/[*?:\[\]\/\\']/g, '') || "Report";
      const worksheet = workbook.addWorksheet(safeSheetName);

      // Add Report header info
      const titleRow = worksheet.addRow(['CausalFunnel Dashboard Generated Report']);
      titleRow.font = { name: 'Arial', size: 14, bold: true };
      
      const timeRow = worksheet.addRow(['Report Generated On: ' + new Date().toLocaleString()]);
      timeRow.font = { name: 'Arial', size: 10, italic: true };
      
      worksheet.addRow([]); // Spacing

      // Add Table Data
      if (data && data.length > 0) {
        data.forEach((row, idx) => {
          const addedRow = worksheet.addRow(row);
          if (idx === 0) {
            // Header row styling
            addedRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            addedRow.eachCell(cell => {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF4F46E5' } // Premium Indigo color header
              };
              cell.alignment = { horizontal: 'center' };
            });
          }
        });
      }

      // Auto-fit column widths
      worksheet.columns.forEach(column => {
        let maxLen = 12;
        column.eachCell({ includeEmpty: false }, cell => {
          const valStr = cell.value ? cell.value.toString() : '';
          if (valStr.length > maxLen) {
            maxLen = valStr.length;
          }
        });
        column.width = maxLen + 3;
      });

      // Embed chart images if references are provided
      const refsToEmbed = [];
      if (chartRef) refsToEmbed.push(chartRef);
      if (chartRefs && chartRefs.length > 0) {
        refsToEmbed.push(...chartRefs);
      }

      let imageRowOffset = 0;
      refsToEmbed.forEach((ref) => {
        if (ref && ref.current && ref.current.canvas) {
          const canvas = ref.current.canvas;
          const imageBase64 = canvas.toDataURL("image/png");

          const imageId = workbook.addImage({
            base64: imageBase64,
            extension: 'png',
          });

          // Insert below the data table (with a 2-row gap + offset for previous images)
          const startRow = (data ? data.length : 0) + 5 + imageRowOffset;
          worksheet.addImage(imageId, {
            tl: { col: 0, row: startRow },
            ext: { width: 650, height: 350 }
          });
          imageRowOffset += 19; // Space for next image
        }
      });

      // Generate Buffer and trigger download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${name.replace(/\s+/g, '_')}.xlsx`;
      link.click();
    } catch (error) {
      console.error("Failed to export Excel sheet:", error);
    }
  };

  // 2. Export to CSV
  const handleExportCSV = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    try {
      const csvData = [
        ['CausalFunnel Dashboard Generated Report'], 
        ['Report Generated On: ' + new Date().toLocaleString()],
        [''], 
        ...data
      ];
      
      const csvContent = csvData
        .map(row => 
          row
            .map(val => {
              if (val === null || val === undefined) return '';
              let cellVal = String(val);
              if (cellVal.includes(',') || cellVal.includes('"') || cellVal.includes('\n')) {
                cellVal = `"${cellVal.replace(/"/g, '""')}"`;
              }
              return cellVal;
            })
            .join(',')
        )
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${name.replace(/\s+/g, '_')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to export CSV:", error);
    }
  };

  // 3. Download Graph
  const handleDownloadGraph = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    if (chartRef && chartRef.current && chartRef.current.canvas) {
      const canvas = chartRef.current.canvas;
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `${name.replace(/\s+/g, '_')}.png`;
      link.href = url;
      link.click();
    }
  };

  // Show download graph if chartRef is provided and has canvas rendering reference
  const showDownloadGraph = !!(chartRef && chartRef.current);

  return (
    <div className={`export-dropdown ${className}`} ref={containerRef} style={style}>
      <button 
        type="button" 
        onClick={toggleDropdown} 
        className="export-dropdown__btn"
      >
        <span className="export-dropdown__btn-content">
          <ExportIcon />
          <span>Export Data</span>
        </span>
        <ArrowDownIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div className="export-dropdown__menu">
          <button 
            type="button" 
            className="export-dropdown__item" 
            onClick={handleExportExcel}
          >
            <ExcelIcon />
            <span>Export to Excel</span>
            <span className="export-dropdown__tooltip">Formatted sheet with embedded chart</span>
          </button>
          <button 
            type="button" 
            className="export-dropdown__item" 
            onClick={handleExportCSV}
          >
            <CSVIcon />
            <span>Export to CSV</span>
            <span className="export-dropdown__tooltip">Raw tabular data for tools & scripts</span>
          </button>
          {showDownloadGraph && (
            <button 
              type="button" 
              className="export-dropdown__item" 
              onClick={handleDownloadGraph}
            >
              <ChartIcon />
              <span>Download Graph</span>
              <span className="export-dropdown__tooltip">High-resolution PNG image of chart</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
